import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft, Key, Bell, Zap, Shield, Check, Trash2, Save, Link2, Power,
  Send, Plus, Terminal, Copy, Radio, ExternalLink, HelpCircle, ChevronDown,
  Loader2, X, User, Mic, Server
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTheme } from "@/contexts/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";
import PageTransition from "@/components/PageTransition";
import { staggerContainer, staggerItem, hoverScale, fadeInUp } from "@/config/animations";
import { ProviderIcon } from "@/components/logos";
import PlatformSettings from "@/components/PlatformSettings";
import { detectProvider, maskKey, PROVIDER_HELP } from "@/utils/apiKeyDetect";
import { useVoiceAgent } from "@/contexts/VoiceAgentContext";
import ProxyKeyManager from "@/components/ProxyKeyManager";
import ConnectToolsGuide from "@/components/ConnectToolsGuide";
import SandboxTryIt from "@/components/SandboxTryIt";
import SecuritySettingsTab from "@/components/SecuritySettingsTab";
import { MyAPIKeysCard } from "@/components/BYOKWizard";
import CustomEndpointsManager from "@/components/CustomEndpointsManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_d04ef3c1-d7de-40d8-b6b3-0d99244d2801/artifacts/0hpd8r65_icon_markremoved%20backround.png";

const TABS = [
  { key: "api-keys", label: "API Keys", icon: Key },
  { key: "custom-endpoints", label: "Self-Hosted Models", icon: Server },
  { key: "preferences", label: "Preferences", icon: Zap },
  { key: "security", label: "Security & Audit", icon: Shield },
  { key: "webhooks", label: "Webhooks", icon: Link2 },
  { key: "profile", label: "Profile", icon: User },
];

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState("api-keys");
  const [settings, setSettings] = useState({
    dwave_api_key: "", openai_api_key: "", anthropic_api_key: "",
    gemini_api_key: "", default_solver: "simulation",
    notifications_enabled: true, auto_optimize: false, has_dwave_key: false,
  });
  const [saving, setSaving] = useState(false);
  const [webhooks, setWebhooks] = useState([]);
  const [newWebhookUrl, setNewWebhookUrl] = useState("");
  const [newWebhookName, setNewWebhookName] = useState("");
  const [mcpKeys, setMcpKeys] = useState([]);
  const [mcpClients, setMcpClients] = useState([]);
  const [newMcpKeyName, setNewMcpKeyName] = useState("");
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);

  // QNNEAL key advanced creation state
  const [advancedKeyOpen, setAdvancedKeyOpen] = useState(false);
  const [newKeyPerms, setNewKeyPerms] = useState("full");
  const [newKeyScopes, setNewKeyScopes] = useState(["LLM-Gateway/chat"]);
  const [newKeyProject, setNewKeyProject] = useState("");
  const [newKeyOwnerType, setNewKeyOwnerType] = useState("user");
  const [newKeyExpiry, setNewKeyExpiry] = useState("never");
  const [scopeCatalog, setScopeCatalog] = useState([]);
  const [editingKeyId, setEditingKeyId] = useState(null);
  const [editingKeyState, setEditingKeyState] = useState(null);

  // Universal key input state
  const [newKeyInput, setNewKeyInput] = useState("");
  const [detectedProvider, setDetectedProvider] = useState(null);
  const [customProviderName, setCustomProviderName] = useState("");
  const [addingKey, setAddingKey] = useState(false);
  const [testingKeys, setTestingKeys] = useState({});
  const [showHelp, setShowHelp] = useState(false);
  const [connectedProviders, setConnectedProviders] = useState([]);
  const [managedKeyInfo, setManagedKeyInfo] = useState(null);

  // Dashboard View preference
  const [dashboardView, setDashboardView] = useState(() => localStorage.getItem("qn_viewMode") || "standard");
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchWebhooks();
    fetchMcpKeys();
    fetchManagedKeyStatus();
  }, []);

  useEffect(() => {
    if (activeTab === "security") fetchAuditLogs();
  }, [activeTab]);

  const fetchAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/audit/logs?limit=50`);
      setAuditLogs(res.data.logs || []);
    } catch {}
    finally { setAuditLoading(false); }
  };

  useEffect(() => {
    if (newKeyInput.length >= 3) {
      setDetectedProvider(detectProvider(newKeyInput));
    } else {
      setDetectedProvider(null);
    }
  }, [newKeyInput]);

  useEffect(() => {
    buildConnectedProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuild connected providers only when settings change
  }, [settings]);

  const fetchManagedKeyStatus = async () => {
    try {
      const res = await axios.get(`${API}/managed-keys/status`);
      setManagedKeyInfo(res.data);
    } catch {}
  };

  // Map of provider_id → frontend display metadata (icons, color).
  // Keeps display details client-side; backend supplies key prefix + name + models.
  const PROVIDER_DISPLAY = {
    openai:     { logo: "openai",     color: "#10A37F" },
    anthropic:  { logo: "anthropic",  color: "#D4A574" },
    gemini:     { logo: "google",     color: "#4285F4" },
    groq:       { logo: "groq",       color: "#F55036" },
    deepseek:   { logo: "deepseek",   color: "#3B82F6" },
    cohere:     { logo: "cohere",     color: "#FF7A59" },
    mistral:    { logo: "mistral",    color: "#FF7000" },
    together:   { logo: "together",   color: "#6366F1" },
    perplexity: { logo: "perplexity", color: "#20808D" },
    fireworks: { logo: "fireworks",   color: "#7C3AED" },
    dwave:      { logo: "dwave",      color: "#0066CC" },
  };

  const buildConnectedProviders = () => {
    // Backend supplies `connected_providers` (preferred). Fall back to boolean flags
    // (for older clients still mid-rollout).
    const fromBackend = Array.isArray(settings.connected_providers) ? settings.connected_providers : null;
    if (fromBackend && fromBackend.length >= 0 && "connected_providers" in settings) {
      setConnectedProviders(fromBackend.map(p => ({
        provider: p.name,
        keyType: p.provider_id,
        logo: PROVIDER_DISPLAY[p.provider_id]?.logo || p.provider_id,
        masked: p.key_prefix,           // e.g. "sk-pro...QcA" — real first6/last3
        models: p.models || [],
        status: p.status || "active",
        added_at: p.added_at,
        last_used_at: p.last_used_at,
        key_label: p.key_label,
      })));
      return;
    }
    // Legacy fallback: derive purely from has_*_key flags
    const providers = [];
    const flagToProvider = {
      has_openai_key: { provider: "OpenAI", keyType: "openai", logo: "openai" },
      has_anthropic_key: { provider: "Anthropic", keyType: "anthropic", logo: "anthropic" },
      has_gemini_key: { provider: "Google Gemini", keyType: "gemini", logo: "google" },
      has_groq_key: { provider: "Groq", keyType: "groq", logo: "groq" },
      has_deepseek_key: { provider: "DeepSeek", keyType: "deepseek", logo: "deepseek" },
      has_cohere_key: { provider: "Cohere", keyType: "cohere", logo: "cohere" },
      has_mistral_key: { provider: "Mistral AI", keyType: "mistral", logo: "mistral" },
      has_together_key: { provider: "Together AI", keyType: "together", logo: "together" },
      has_perplexity_key: { provider: "Perplexity", keyType: "perplexity", logo: "perplexity" },
      has_fireworks_key: { provider: "Fireworks AI", keyType: "fireworks", logo: "fireworks" },
      has_dwave_key: { provider: "D-Wave", keyType: "dwave", logo: "dwave" },
    };
    Object.entries(flagToProvider).forEach(([flag, meta]) => {
      if (settings[flag]) providers.push({ ...meta, masked: "••••••••" });
    });
    setConnectedProviders(providers);
  };

  const fetchMcpKeys = async () => {
    try {
      const [keysRes, clientsRes, scopesRes] = await Promise.all([
        axios.get(`${API}/mcp/keys`),
        axios.get(`${API}/mcp/clients`),
        axios.get(`${API}/mcp/keys/scopes`).catch(() => ({ data: { scopes: [] } })),
      ]);
      setMcpKeys(keysRes.data);
      setMcpClients(clientsRes.data);
      setScopeCatalog(scopesRes.data.scopes || []);
    } catch (_e) { /* noop */ }
  };

  const _expiryToIso = (choice) => {
    if (choice === "never" || !choice) return null;
    const days = choice === "30d" ? 30 : choice === "90d" ? 90 : null;
    if (!days) return null;
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString();
  };

  const toggleNewKeyScope = (scopeId) => {
    setNewKeyScopes((prev) =>
      prev.includes(scopeId) ? prev.filter((s) => s !== scopeId) : [...prev, scopeId]
    );
  };

  const handleCreateMcpKey = async () => {
    try {
      if (newKeyPerms === "restricted" && newKeyScopes.length === 0) {
        toast.error("Restricted keys require at least one scope");
        return;
      }
      const body = {
        name: newMcpKeyName || "Default",
        permissions: newKeyPerms,
        scopes: newKeyPerms === "restricted" ? newKeyScopes : null,
        project: newKeyProject || null,
        owner_type: newKeyOwnerType,
        expires_at: _expiryToIso(newKeyExpiry),
      };
      const response = await axios.post(`${API}/mcp/keys`, body);
      setNewlyCreatedKey(response.data.api_key);
      setNewMcpKeyName("");
      setAdvancedKeyOpen(false);
      // Reset advanced fields to safe defaults for next creation
      setNewKeyPerms("full");
      setNewKeyScopes(["LLM-Gateway/chat"]);
      setNewKeyProject("");
      setNewKeyOwnerType("user");
      setNewKeyExpiry("never");
      toast.success("QNNEAL API key generated. Copy it now — you won't see it again.");
      fetchMcpKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate key");
    }
  };

  const handleUpdateMcpKeyPermissions = async () => {
    if (!editingKeyId || !editingKeyState) return;
    try {
      const body = {
        key_id: editingKeyId,
        permissions: editingKeyState.permissions,
        scopes: editingKeyState.permissions === "restricted" ? (editingKeyState.scopes || []) : null,
        project: editingKeyState.project || null,
      };
      await axios.put(`${API}/mcp/keys/${editingKeyId}/permissions`, body);
      toast.success("Permissions updated");
      setEditingKeyId(null);
      setEditingKeyState(null);
      fetchMcpKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update permissions");
    }
  };

  const handleRevokeMcpKey = async (keyId) => {
    try {
      await axios.delete(`${API}/mcp/keys/${keyId}`);
      toast.success("Key revoked");
      fetchMcpKeys();
    } catch { toast.error("Failed to revoke key"); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const fetchWebhooks = async () => {
    try { setWebhooks((await axios.get(`${API}/webhooks`)).data); } catch {}
  };

  const handleCreateWebhook = async () => {
    if (!newWebhookUrl) { toast.error("Enter a webhook URL"); return; }
    try {
      await axios.post(`${API}/webhooks`, { url: newWebhookUrl, name: newWebhookName || undefined });
      toast.success("Webhook registered");
      setNewWebhookUrl(""); setNewWebhookName("");
      fetchWebhooks();
    } catch { toast.error("Failed to create webhook"); }
  };

  const handleDeleteWebhook = async (webhookId) => {
    try { await axios.delete(`${API}/webhooks/${webhookId}`); toast.success("Webhook deleted"); fetchWebhooks(); }
    catch { toast.error("Failed to delete webhook"); }
  };

  const handleToggleWebhook = async (webhookId) => {
    try {
      const r = await axios.put(`${API}/webhooks/${webhookId}/toggle`);
      toast.success(r.data.active ? "Webhook enabled" : "Webhook paused");
      fetchWebhooks();
    } catch { toast.error("Failed to toggle webhook"); }
  };

  const handleTestWebhook = async (webhookId) => {
    try {
      const r = await axios.post(`${API}/webhooks/${webhookId}/test`);
      r.data.success ? toast.success(`Test ping sent (HTTP ${r.data.status_code})`) : toast.error(`Test failed: ${r.data.error}`);
    } catch { toast.error("Test ping failed"); }
  };

  const fetchSettings = async () => {
    try { setSettings((await axios.get(`${API}/settings`)).data); } catch {}
  };

  const handleAddKey = async () => {
    if (!newKeyInput || newKeyInput.length < 5) { toast.error("Please enter a valid API key"); return; }
    const detected = detectProvider(newKeyInput);
    if (!detected) return;

    setAddingKey(true);
    try {
      // detected.keyType is the canonical provider_id from apiKeyDetect.js
      // (openai/anthropic/gemini/groq/deepseek/cohere/mistral/together/perplexity/fireworks/dwave/xai/openrouter/custom)
      const SUPPORTED = new Set([
        "openai", "anthropic", "gemini", "groq", "deepseek",
        "cohere", "mistral", "together", "perplexity", "fireworks", "dwave",
      ]);
      const keyType = detected.keyType;
      if (!SUPPORTED.has(keyType)) {
        toast.error(`${detected.provider} keys are detected but not yet routable. Use OpenAI/Anthropic/Gemini/Groq/DeepSeek/Cohere/Mistral/Together/Perplexity/Fireworks/D-Wave for now.`);
        setAddingKey(false);
        return;
      }
      const keyField = `${keyType}_api_key`;
      await axios.put(`${API}/settings`, { [keyField]: newKeyInput });
      toast.success(`${detected.provider} key added successfully`);
      setNewKeyInput("");
      setDetectedProvider(null);
      setCustomProviderName("");
      fetchSettings();
      fetchManagedKeyStatus();
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to save API key";
      toast.error(detail);
    }
    finally { setAddingKey(false); }
  };

  const handleTestKey = async (keyType) => {
    setTestingKeys(prev => ({ ...prev, [keyType]: "testing" }));
    try {
      await axios.post(`${API}/settings/test-key/${keyType}`);
      setTestingKeys(prev => ({ ...prev, [keyType]: "success" }));
      setTimeout(() => setTestingKeys(prev => ({ ...prev, [keyType]: null })), 3000);
    } catch {
      setTestingKeys(prev => ({ ...prev, [keyType]: "failed" }));
      setTimeout(() => setTestingKeys(prev => ({ ...prev, [keyType]: null })), 3000);
    }
  };

  const handleDeleteKey = async (keyType) => {
    try {
      await axios.delete(`${API}/settings/api-key/${keyType}`);
      toast.success(`${keyType} API key removed`);
      fetchSettings();
      fetchManagedKeyStatus();
    } catch { toast.error("Failed to delete API key"); }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, {
        default_solver: settings.default_solver,
        notifications_enabled: settings.notifications_enabled,
        auto_optimize: settings.auto_optimize,
      });
      localStorage.setItem("qn_viewMode", dashboardView);
      toast.success("Preferences saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-[var(--qn-background)]">
        {/* Header */}
        <motion.header
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="glass border-b border-[var(--qn-border)]"
        >
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-btn" data-automation-id="settings-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate("/")}>
                <img src={LOGO_ICON} alt="QNNEAL" className="h-10 w-10 object-contain" />
                <span className="font-outfit font-bold text-2xl tracking-tight">QNNEAL</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <h1 className="font-outfit font-semibold text-xl">Settings</h1>
            </div>
          </div>
        </motion.header>

        {/* Tab Bar */}
        <div className="border-b border-[var(--qn-border)] bg-[var(--qn-card)]">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex gap-1" data-testid="settings-tabs">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  data-testid={`settings-tab-${tab.key}`}
                  data-automation-id={`settings-tab-${tab.key}`}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 ${
                    activeTab === tab.key
                      ? "border-[var(--qn-primary)] text-[var(--qn-primary)]"
                      : "border-transparent text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
          <AnimatePresence mode="wait">
            {/* ─── API KEYS TAB ─── */}
            {activeTab === "api-keys" && (
              <motion.div key="api-keys" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                {/* QNNEAL Proxy Key + Connect Tools */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                  <ProxyKeyManager />
                  <ConnectToolsGuide />
                </div>

                {/* Try It Now Sandbox */}
                <div className="mb-6">
                  <SandboxTryIt compact={true} />
                </div>

                {/* Universal Key Input */}
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] mb-6">
                  <CardHeader>
                    <CardTitle className="font-outfit flex items-center gap-3">
                      <Key className="w-5 h-5 text-[var(--qn-primary)]" />
                      Your API Keys
                    </CardTitle>
                    <CardDescription className="text-[var(--qn-foreground-muted)]">
                      Paste any AI provider key. We auto-detect the rest.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          value={newKeyInput}
                          onChange={e => setNewKeyInput(e.target.value)}
                          placeholder="Paste your API key here..."
                          className="bg-[var(--qn-background)] border-[var(--qn-border)] pr-12"
                          data-testid="universal-key-input"
                          data-automation-id="universal-key-input"
                          onKeyDown={e => e.key === "Enter" && handleAddKey()}
                        />
                        {detectedProvider && (
                          <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 25 }}
                            className="absolute right-3 top-1/2 -translate-y-1/2"
                          >
                            <ProviderIcon providerKey={detectedProvider.logo} size="sm" variant="color" />
                          </motion.div>
                        )}
                      </div>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          onClick={handleAddKey}
                          disabled={!newKeyInput || addingKey}
                          className="btn-primary px-6"
                          data-testid="add-key-btn"
                          data-automation-id="add-key-btn"
                        >
                          {addingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Add <ArrowLeft className="w-4 h-4 ml-1 rotate-180" /></>}
                        </Button>
                      </motion.div>
                    </div>

                    {detectedProvider && (
                      <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-sm">
                        <span className="text-[var(--qn-foreground-muted)]">Detected: </span>
                        <span className="font-medium" style={{ color: detectedProvider.color }}>{detectedProvider.provider}</span>
                      </motion.div>
                    )}

                    {detectedProvider?.provider === "Custom Provider" && (
                      <Input
                        value={customProviderName}
                        onChange={e => setCustomProviderName(e.target.value)}
                        placeholder="Name this provider (optional)"
                        className="bg-[var(--qn-background)] border-[var(--qn-border)] max-w-xs"
                        data-testid="custom-provider-name"
                      />
                    )}

                    {/* Show me how */}
                    <button
                      onClick={() => setShowHelp(!showHelp)}
                      className="flex items-center gap-2 text-sm text-[var(--qn-primary)] hover:underline"
                      data-testid="show-help-btn"
                      data-automation-id="show-help-btn"
                    >
                      <HelpCircle className="w-4 h-4" />
                      Don't know where to find your key?
                      <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showHelp ? "rotate-180" : ""}`} />
                    </button>

                    <AnimatePresence>
                      {showHelp && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-3 pt-2" data-testid="settings-help-panel">
                            {PROVIDER_HELP.map(p => (
                              <div key={p.provider} className="bg-[var(--qn-background)] rounded-lg p-4 border border-[var(--qn-border)]">
                                <div className="flex items-center gap-2 mb-3">
                                  <ProviderIcon providerKey={p.logo} size="sm" variant="color" />
                                  <span className="font-medium text-sm">{p.provider}</span>
                                </div>
                                <ol className="list-decimal list-inside space-y-1 text-sm text-[var(--qn-foreground-muted)]">
                                  {p.steps.map((s, i) => <li key={i}>{s}</li>)}
                                </ol>
                                {p.url && (
                                  <div className="mt-3 flex items-center gap-3">
                                    <a
                                      href={p.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-[var(--qn-primary)] hover:underline flex items-center gap-1"
                                      data-testid={`help-link-${p.logo}`}
                                    >
                                      {p.urlLabel} <ExternalLink className="w-3 h-3" />
                                    </a>
                                    <button
                                      onClick={() => toast.info("Video guide coming soon!")}
                                      className="text-sm text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] flex items-center gap-1"
                                    >
                                      Watch 30s video
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}

                            {/* Voice Guide Hook */}
                            <div className="bg-[var(--qn-primary-muted)] rounded-lg p-4 border border-[var(--qn-primary)]/20">
                              <div className="flex items-center gap-2 mb-2">
                                <Mic className="w-4 h-4 text-[var(--qn-primary)]" />
                                <span className="text-sm font-medium text-[var(--qn-primary)]">Need hands-free help?</span>
                              </div>
                              <p className="text-sm text-[var(--qn-foreground-muted)]">
                                Our AI assistant can walk you through it step by step with voice guidance.
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                className="mt-2 border-[var(--qn-primary)]/30 text-[var(--qn-primary)]"
                                onClick={() => {
                                  const va = document.querySelector('[data-testid="floating-help-btn"]');
                                  if (va) va.click();
                                }}
                                data-testid="voice-guide-btn"
                              >
                                <Mic className="w-3 h-3 mr-1.5" />
                                Launch Voice Guide
                                <Badge className="ml-2 text-[10px] bg-[var(--qn-primary)]/20 text-[var(--qn-primary)]">Soon</Badge>
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>

                {/* Connected Providers */}
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] mb-6">
                  <CardHeader className="pb-3">
                    <CardTitle className="font-outfit text-base">
                      Connected Providers ({connectedProviders.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {connectedProviders.length > 0 ? (
                      <div className="space-y-3">
                        {connectedProviders.map(p => (
                          <div key={p.keyType} className="flex items-center gap-3 bg-[var(--qn-background)] rounded-lg p-3 border border-[var(--qn-border)]" data-testid={`provider-${p.keyType}`}>
                            <ProviderIcon providerKey={p.logo} size="sm" variant="color" />
                            <div className="flex-1">
                              <div className="font-medium text-sm">{p.provider}</div>
                              <div className="text-xs text-[var(--qn-foreground-subtle)] font-mono">{p.masked}</div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Button
                                variant="outline" size="sm"
                                className={`text-xs h-7 ${testingKeys[p.keyType] === "success" ? "border-[var(--qn-success)] text-[var(--qn-success)]" : testingKeys[p.keyType] === "failed" ? "border-red-500 text-red-400" : "border-[var(--qn-border)]"}`}
                                onClick={() => handleTestKey(p.keyType)}
                                disabled={testingKeys[p.keyType] === "testing"}
                                data-testid={`test-key-${p.keyType}`}
                              >
                                {testingKeys[p.keyType] === "testing" ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : testingKeys[p.keyType] === "success" ? (
                                  <><Check className="w-3 h-3 mr-1" /> Connected</>
                                ) : testingKeys[p.keyType] === "failed" ? (
                                  <><X className="w-3 h-3 mr-1" /> Failed</>
                                ) : "Test"}
                              </Button>
                              <Button
                                variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:bg-red-500/10"
                                onClick={() => handleDeleteKey(p.keyType)}
                                data-testid={`delete-key-${p.keyType}`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-sm text-[var(--qn-foreground-subtle)]">
                        No providers connected yet. Paste a key above to get started.
                      </div>
                    )}
                    {managedKeyInfo && (
                      <div className="mt-3 text-xs text-[var(--qn-foreground-subtle)]">
                        {connectedProviders.length} provider{connectedProviders.length !== 1 ? "s" : ""} connected
                        {!managedKeyInfo.has_own_keys && ` · ${managedKeyInfo.remaining} free optimizations left`}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* QNNEAL API Key (MCP) */}
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  <CardHeader>
                    <CardTitle className="font-outfit flex items-center gap-3 text-base">
                      <Terminal className="w-5 h-5 text-[var(--qn-secondary)]" />
                      QNNEAL API Key
                    </CardTitle>
                    <CardDescription className="text-[var(--qn-foreground-muted)]">
                      Use this key to integrate QNNEAL optimization into your apps via REST API or MCP server.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        value={newMcpKeyName}
                        onChange={e => setNewMcpKeyName(e.target.value)}
                        placeholder="Key name (e.g. Cursor, Claude, LintelAQ)"
                        className="flex-1 bg-[var(--qn-background)] border-[var(--qn-border)]"
                        data-testid="mcp-key-name-input"
                      />
                      <Button onClick={handleCreateMcpKey} className="btn-primary" data-testid="generate-mcp-key-btn">
                        <Plus className="w-4 h-4 mr-2" /> Generate Key
                      </Button>
                    </div>

                    {/* Customize permissions disclosure */}
                    <button
                      type="button"
                      onClick={() => setAdvancedKeyOpen(o => !o)}
                      data-testid="customize-permissions-toggle"
                      className="text-xs text-[var(--qn-primary)] hover:underline flex items-center gap-1"
                    >
                      <Shield className="w-3 h-3" />
                      {advancedKeyOpen ? "Hide permissions" : "Customize permissions, project, expiry…"}
                    </button>

                    {advancedKeyOpen && (
                      <div className="border border-[var(--qn-border)] rounded-lg p-4 space-y-4 bg-[var(--qn-background)]" data-testid="advanced-key-panel">
                        {/* Owner */}
                        <div>
                          <label className="text-xs font-medium">Owned by</label>
                          <div className="flex gap-2 mt-1">
                            {[{id:"user",label:"You"},{id:"service_account",label:"Service account"}].map(opt => (
                              <button key={opt.id} onClick={() => setNewKeyOwnerType(opt.id)}
                                data-testid={`mcp-owner-${opt.id}`}
                                className={`px-3 py-1 rounded text-xs border ${newKeyOwnerType === opt.id ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]/10 text-[var(--qn-primary)]" : "border-[var(--qn-border)] text-[var(--qn-foreground-muted)]"}`}>{opt.label}</button>
                            ))}
                          </div>
                        </div>
                        {/* Project */}
                        <div>
                          <label className="text-xs font-medium">Project</label>
                          <select
                            value={newKeyProject}
                            onChange={e => setNewKeyProject(e.target.value)}
                            data-testid="mcp-project-select"
                            className="mt-1 w-full px-2 py-1.5 bg-[var(--qn-card)] border border-[var(--qn-border)] rounded text-xs"
                          >
                            <option value="">All Projects</option>
                            <option value="HAVEN">HAVEN</option>
                            <option value="Vista Portfolio">Vista Portfolio</option>
                            <option value="H&F Portfolio">H&F Portfolio</option>
                            <option value="XIUS Integration">XIUS Integration</option>
                          </select>
                        </div>
                        {/* Permission level */}
                        <div>
                          <label className="text-xs font-medium">Permissions</label>
                          <div className="flex gap-2 mt-1">
                            {[{id:"full",label:"All"},{id:"restricted",label:"Restricted"},{id:"read_only",label:"Read only"}].map(opt => (
                              <button key={opt.id} onClick={() => setNewKeyPerms(opt.id)}
                                data-testid={`mcp-perm-${opt.id}`}
                                className={`px-3 py-1 rounded text-xs border ${newKeyPerms === opt.id ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]/10 text-[var(--qn-primary)]" : "border-[var(--qn-border)] text-[var(--qn-foreground-muted)]"}`}>{opt.label}</button>
                            ))}
                          </div>
                        </div>
                        {/* Scopes */}
                        {newKeyPerms === "restricted" && (
                          <div data-testid="mcp-scope-checkboxes">
                            <label className="text-xs font-medium">Scopes</label>
                            <p className="text-[10px] text-[var(--qn-foreground-muted)] mt-0.5">
                              <span className="text-[var(--qn-primary)]">LLM-Gateway/chat</span> is the core scope for routing chat requests through QNNEAL.
                            </p>
                            <div className="mt-2 space-y-1 max-h-48 overflow-y-auto border border-[var(--qn-border)] rounded p-2 bg-[var(--qn-card)]">
                              {scopeCatalog.map(s => (
                                <label key={s.id} className="flex items-start gap-2 cursor-pointer p-1 hover:bg-[var(--qn-background)] rounded" data-testid={`mcp-scope-${s.id}`}>
                                  <input type="checkbox" checked={newKeyScopes.includes(s.id)}
                                    onChange={() => toggleNewKeyScope(s.id)}
                                    className="mt-0.5 accent-[var(--qn-primary)]" />
                                  <div className="text-xs">
                                    <div>{s.label}</div>
                                    <div className="text-[var(--qn-foreground-muted)]">{s.description}</div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Expiration */}
                        <div>
                          <label className="text-xs font-medium">Expiration</label>
                          <div className="flex gap-2 mt-1">
                            {[{id:"never",label:"Never"},{id:"30d",label:"30 days"},{id:"90d",label:"90 days"}].map(opt => (
                              <button key={opt.id} onClick={() => setNewKeyExpiry(opt.id)}
                                data-testid={`mcp-expiry-${opt.id}`}
                                className={`px-3 py-1 rounded text-xs border ${newKeyExpiry === opt.id ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]/10 text-[var(--qn-primary)]" : "border-[var(--qn-border)] text-[var(--qn-foreground-muted)]"}`}>{opt.label}</button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {newlyCreatedKey && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-[var(--qn-success)]/10 border border-[var(--qn-success)]/30 rounded-lg p-4">
                        <div className="text-sm font-medium text-[var(--qn-success)] mb-2">New API Key Generated</div>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 font-mono text-sm bg-[var(--qn-background)] rounded px-3 py-2 overflow-x-auto">{newlyCreatedKey}</code>
                          <Button size="icon" variant="ghost" onClick={() => copyToClipboard(newlyCreatedKey)} data-testid="copy-mcp-key-btn">
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-[var(--qn-foreground-subtle)] mt-2">Copy this key now. It won&apos;t be shown again.</p>
                      </motion.div>
                    )}
                    {mcpKeys.length > 0 && (
                      <div className="space-y-2">
                        {mcpKeys.map(k => {
                          const scopes = Array.isArray(k.scopes) && k.scopes.length ? k.scopes : ["*"];
                          const isFull = scopes.includes("*");
                          const isEditing = editingKeyId === k.key_id;
                          return (
                          <div key={k.key_id} className="bg-[var(--qn-background)] rounded-lg p-3 border border-[var(--qn-border)] space-y-2" data-testid={`mcp-key-row-${k.key_id}`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${k.revoked ? "bg-red-400" : "bg-[var(--qn-success)]"}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-sm">{k.name}</span>
                                  <Badge className="text-[9px]" variant="outline">{k.permissions || "full"}</Badge>
                                  {k.project && <Badge className="text-[9px] bg-purple-500/10 text-purple-300 border-purple-500/30" variant="outline">{k.project}</Badge>}
                                  {k.owner_type === "service_account" && <Badge className="text-[9px] bg-blue-500/10 text-blue-300 border-blue-500/30" variant="outline">service account</Badge>}
                                </div>
                                <div className="text-xs text-[var(--qn-foreground-subtle)] font-mono mt-0.5">{k.api_key_masked}</div>
                                <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`mcp-scopes-${k.key_id}`}>
                                  {isFull ? (
                                    <Badge className="text-[9px] bg-emerald-500/10 text-emerald-300 border-emerald-500/30" variant="outline">
                                      <Shield className="w-2.5 h-2.5 mr-1" /> All scopes
                                    </Badge>
                                  ) : (
                                    scopes.map(s => <Badge key={s} className="text-[9px] bg-teal-500/10 text-teal-300 border-teal-500/30" variant="outline">{s}</Badge>)
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-1">
                                {!k.revoked && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)]"
                                    onClick={() => {
                                      if (isEditing) { setEditingKeyId(null); setEditingKeyState(null); }
                                      else { setEditingKeyId(k.key_id); setEditingKeyState({ permissions: k.permissions || "full", scopes: [...scopes], project: k.project }); }
                                    }}
                                    data-testid={`edit-mcp-key-${k.key_id}`}
                                    title="Edit permissions"
                                  >
                                    <Shield className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                {!k.revoked ? (
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => handleRevokeMcpKey(k.key_id)} data-testid={`revoke-mcp-key-${k.key_id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                ) : (
                                  <Badge className="bg-red-500/15 text-red-400 text-xs">Revoked</Badge>
                                )}
                              </div>
                            </div>
                            {isEditing && editingKeyState && (
                              <div className="border-t border-[var(--qn-border)] pt-3 space-y-3" data-testid={`edit-mcp-panel-${k.key_id}`}>
                                <div>
                                  <label className="text-[10px] uppercase text-[var(--qn-foreground-muted)]">Permission level</label>
                                  <div className="flex gap-2 mt-1">
                                    {[{id:"full",label:"All"},{id:"restricted",label:"Restricted"},{id:"read_only",label:"Read only"}].map(opt => (
                                      <button key={opt.id}
                                        onClick={() => setEditingKeyState({ ...editingKeyState, permissions: opt.id })}
                                        data-testid={`edit-mcp-perm-${opt.id}-${k.key_id}`}
                                        className={`px-3 py-1 rounded text-xs border ${editingKeyState.permissions === opt.id ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]/10 text-[var(--qn-primary)]" : "border-[var(--qn-border)] text-[var(--qn-foreground-muted)]"}`}>{opt.label}</button>
                                    ))}
                                  </div>
                                </div>
                                {editingKeyState.permissions === "restricted" && (
                                  <div className="max-h-40 overflow-y-auto border border-[var(--qn-border)] rounded p-2 space-y-1">
                                    {scopeCatalog.map(s => (
                                      <label key={s.id} className="flex items-start gap-2 cursor-pointer p-0.5 text-xs">
                                        <input type="checkbox"
                                          checked={(editingKeyState.scopes || []).includes(s.id)}
                                          onChange={() => {
                                            const cur = editingKeyState.scopes || [];
                                            setEditingKeyState({ ...editingKeyState, scopes: cur.includes(s.id) ? cur.filter(x => x !== s.id) : [...cur, s.id] });
                                          }}
                                          data-testid={`edit-mcp-scope-${s.id}-${k.key_id}`}
                                          className="mt-0.5 accent-[var(--qn-primary)]"
                                        />
                                        <div>
                                          <div>{s.label}</div>
                                          <div className="text-[var(--qn-foreground-muted)] text-[10px]">{s.description}</div>
                                        </div>
                                      </label>
                                    ))}
                                  </div>
                                )}
                                <div>
                                  <label className="text-[10px] uppercase text-[var(--qn-foreground-muted)]">Project</label>
                                  <select
                                    value={editingKeyState.project || ""}
                                    onChange={e => setEditingKeyState({ ...editingKeyState, project: e.target.value || null })}
                                    data-testid={`edit-mcp-project-${k.key_id}`}
                                    className="mt-1 w-full px-2 py-1 text-xs bg-[var(--qn-card)] border border-[var(--qn-border)] rounded"
                                  >
                                    <option value="">All Projects</option>
                                    <option value="HAVEN">HAVEN</option>
                                    <option value="Vista Portfolio">Vista Portfolio</option>
                                    <option value="H&F Portfolio">H&F Portfolio</option>
                                    <option value="XIUS Integration">XIUS Integration</option>
                                  </select>
                                </div>
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={handleUpdateMcpKeyPermissions} className="btn-primary" data-testid={`save-mcp-perms-${k.key_id}`}>Save changes</Button>
                                  <Button size="sm" variant="ghost" onClick={() => { setEditingKeyId(null); setEditingKeyState(null); }}>Cancel</Button>
                                </div>
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* ─── CUSTOM ENDPOINTS TAB (Gemma 4 / Self-Hosted) ─── */}
            {activeTab === "custom-endpoints" && (
              <motion.div key="custom-endpoints" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <CustomEndpointsManager />
              </motion.div>
            )}

            {/* ─── PREFERENCES TAB ─── */}
            {activeTab === "preferences" && (
              <motion.div key="preferences" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
                {/* My API Keys Card */}
                <MyAPIKeysCard />
                {/* Dashboard View */}
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  <CardHeader>
                    <CardTitle className="font-outfit flex items-center gap-3">
                      <Shield className="w-5 h-5 text-[var(--qn-primary)]" />
                      Dashboard View
                    </CardTitle>
                    <CardDescription className="text-[var(--qn-foreground-muted)]">
                      Choose how much detail you see on the dashboard
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-3" data-testid="dashboard-view-selector">
                      {[
                        { key: "executive", label: "Executive", desc: "Cost savings and ROI at a glance. Best for decision-makers." },
                        { key: "standard", label: "Standard", desc: "Balanced view of savings, workflows, and activity. Recommended." },
                        { key: "engineer", label: "Engineer", desc: "Full technical detail including model routing and token counts." },
                      ].map(v => (
                        <button
                          key={v.key}
                          onClick={() => setDashboardView(v.key)}
                          data-testid={`view-${v.key}`}
                          className={`text-left p-4 rounded-lg border transition-all duration-200 ${
                            dashboardView === v.key
                              ? "border-[var(--qn-primary)] bg-[var(--qn-primary-muted)]"
                              : "border-[var(--qn-border)] hover:border-[var(--qn-foreground-subtle)]"
                          }`}
                        >
                          <div className="font-medium text-sm mb-1">{v.label}</div>
                          <div className="text-xs text-[var(--qn-foreground-muted)] leading-relaxed">{v.desc}</div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Platform Settings */}
                <PlatformSettings />

                {/* Solver & Notifications */}
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  <CardHeader>
                    <CardTitle className="font-outfit flex items-center gap-3">
                      <Zap className="w-5 h-5 text-[var(--qn-secondary)]" />
                      Optimization Preferences
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="font-medium">Default Solver</Label>
                        <p className="text-sm text-[var(--qn-foreground-subtle)]">Simulation or real quantum hardware</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant={settings.default_solver === "simulation" ? "default" : "outline"} size="sm"
                          onClick={() => setSettings(s => ({ ...s, default_solver: "simulation" }))}
                          className={settings.default_solver === "simulation" ? "bg-[var(--qn-primary)] text-white" : ""}
                        >Simulation</Button>
                        <Button
                          variant={settings.default_solver === "quantum" ? "default" : "outline"} size="sm"
                          onClick={() => setSettings(s => ({ ...s, default_solver: "quantum" }))}
                          disabled={!settings.has_dwave_key}
                          className={settings.default_solver === "quantum" ? "bg-[var(--qn-secondary)] text-white" : ""}
                        >Quantum</Button>
                      </div>
                    </div>
                    <Separator className="bg-[var(--qn-border)]" />
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="font-medium flex items-center gap-2"><Bell className="w-4 h-4" /> Real-time Notifications</Label>
                        <p className="text-sm text-[var(--qn-foreground-subtle)]">WebSocket notifications for optimization progress</p>
                      </div>
                      <Switch checked={settings.notifications_enabled} onCheckedChange={v => setSettings(s => ({ ...s, notifications_enabled: v }))} data-testid="notifications-switch" />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="font-medium flex items-center gap-2"><Zap className="w-4 h-4" /> Auto-Optimize</Label>
                        <p className="text-sm text-[var(--qn-foreground-subtle)]">Automatically optimize workflows on creation</p>
                      </div>
                      <Switch checked={settings.auto_optimize} onCheckedChange={v => setSettings(s => ({ ...s, auto_optimize: v }))} disabled={user?.subscription_tier === "starter"} data-testid="auto-optimize-switch" />
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-end">
                  <Button onClick={handleSavePreferences} disabled={saving} className="btn-primary px-8" data-testid="save-settings-btn">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Preferences"}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ─── SECURITY & AUDIT TAB ─── */}
            {activeTab === "security" && (
              <motion.div key="security" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <SecuritySettingsTab />
              </motion.div>
            )}

            {/* ─── WEBHOOKS TAB ─── */}
            {activeTab === "webhooks" && (
              <motion.div key="webhooks" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  <CardHeader>
                    <CardTitle className="font-outfit flex items-center gap-3">
                      <Link2 className="w-5 h-5 text-[var(--qn-primary)]" />
                      Webhook Notifications
                    </CardTitle>
                    <CardDescription className="text-[var(--qn-foreground-muted)]">
                      Get notified via HTTP POST when optimizations complete
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex gap-2">
                      <Input value={newWebhookName} onChange={e => setNewWebhookName(e.target.value)} placeholder="Name (optional)" className="w-40 bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="webhook-name-input" />
                      <Input value={newWebhookUrl} onChange={e => setNewWebhookUrl(e.target.value)} placeholder="https://your-server.com/webhook" className="flex-1 bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="webhook-url-input" />
                      <Button onClick={handleCreateWebhook} className="btn-primary" data-testid="add-webhook-btn">
                        <Plus className="w-4 h-4 mr-2" /> Add
                      </Button>
                    </div>
                    {webhooks.length > 0 ? (
                      <div className="space-y-3">
                        {webhooks.map(wh => (
                          <div key={wh.webhook_id} className="flex items-center gap-3 bg-[var(--qn-background)] rounded-lg p-4 border border-[var(--qn-border)]">
                            <div className={`w-2 h-2 rounded-full ${wh.active ? "bg-[var(--qn-success)]" : "bg-[var(--qn-foreground-subtle)]"}`} />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">{wh.name}</div>
                              <div className="text-xs text-[var(--qn-foreground-subtle)] font-mono truncate">{wh.url}</div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleTestWebhook(wh.webhook_id)}><Send className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleToggleWebhook(wh.webhook_id)}>
                                <Power className={`w-4 h-4 ${wh.active ? "text-[var(--qn-success)]" : "text-[var(--qn-foreground-subtle)]"}`} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => handleDeleteWebhook(wh.webhook_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-sm text-[var(--qn-foreground-subtle)]">No webhooks configured.</div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* ─── PROFILE TAB ─── */}
            {activeTab === "profile" && (
              <motion.div key="profile" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  <CardHeader>
                    <CardTitle className="font-outfit flex items-center gap-3">
                      <User className="w-5 h-5 text-[var(--qn-primary)]" />
                      Profile
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-[var(--qn-primary-muted)] flex items-center justify-center text-2xl font-bold text-[var(--qn-primary)]">
                        {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-lg">{user?.name || "User"}</div>
                        <div className="text-sm text-[var(--qn-foreground-muted)]">{user?.email || "test@qnneal.dev"}</div>
                        <Badge className="mt-1 bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] text-xs">
                          {user?.subscription_tier || "starter"} plan
                        </Badge>
                      </div>
                    </div>
                    <Separator className="bg-[var(--qn-border)]" />
                    <div className="text-sm text-[var(--qn-foreground-subtle)]">
                      QNNEAL retains optimization logs for 90 days. API keys are encrypted at rest. You can export or delete your data at any time.
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </PageTransition>
  );
}
